// Core imports
import React, { useState, useRef, useEffect } from "react";

// Components and assets
import { ProductFormImage } from "./ProductFormImage";
import Modal from "./Modal";

// DnD-kit imports for drag and drop functionality
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToParentElement } from "@dnd-kit/modifiers";

// Icons
import { PiUploadFill } from "react-icons/pi";

const MultipleImageInput = ({ pOldImages, setPOldImages, setProductImages, setRemovedImages, iOrder, setIOrder }) => {
  // Local state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  // const [pOldImages, setPOldImages] = useState([]);
  const [newOrder, setNewOrder] = useState([]);

  const [lastRemovedIndex, setLastRemovedIndex] = useState(-1);
  const [removeCount, setRemoveCount] = useState(0);

  const scrollableDivRef = useRef(null);


  useEffect(() => {
    setIOrder([]);
    pOldImages?.map((image) => {
      setIOrder((previousOrder) => {
        if(image.id.startsWith("new")) return [...previousOrder, Number(image.id.replace("new-", ""))];
        return [...previousOrder, Number(image.id.replace("old-", "")) + 10];
      });
    });
  }, [pOldImages]);

  useEffect(() => {
    const newValues = iOrder?.filter(value => value < 10) || [];
    setNewOrder(newValues);
  }, [iOrder]);

  // Configure DnD sensors
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, }));

  const handleRemoveOldImage = ({ id, index }) => {
    // Handle removal of newly added images
    if (id.startsWith("new")) setProductImages(prevImages => [...prevImages].splice(Number(id.replace("new-", "")), 1));
    // Update display images array
    setPOldImages(prevImages => {
      const updatedImages = [...prevImages];
      const adjustedIndex = index >= lastRemovedIndex ? index + removeCount : index;
      updatedImages.splice(adjustedIndex, 1);
      setLastRemovedIndex(adjustedIndex);
      setRemoveCount(prev => prev + 1);
      if (id.startsWith("old")) setRemovedImages(prev => [...prev, Number(id.replace("old-", ""))]);
      return updatedImages;
    });
  };

  /**
   * Handles horizontal scrolling with mouse wheel
   * @param {WheelEvent} event 
   */
  const handleWheel = (event) => {
    const wheelDelta = Math.max(-1, Math.min(1, event.deltaY || -event.detail));
    if (scrollableDivRef.current) scrollableDivRef.current.scrollLeft += wheelDelta * 30;
  };

  /**
   * Updates both display and storage arrays with new images
   * @param {File[]} imgSrcList - Array of image files
   */
  const updateImages = (imgSrcList) => {
    // Update actual image files array (limited to 5 images)
    setProductImages(prevImages => [...prevImages, ...imgSrcList].slice(0, 5));
    // Update display images array with preview URLs
    setPOldImages(prevImages => {
      const totalDisplay = [...prevImages, ...imgSrcList?.map((image, index) => ({
          id: `new-${index + newOrder.length}`,
          url: URL.createObjectURL(image),
        })),
      ].slice(0, 5);
      return totalDisplay;
    });
  };

  // Resets modal and file input state
  const closeModal = () => {
    setModalOpen(false);
    setSelectedFiles([]);
    const fileInput = document.getElementById("imageUpload");
    if (fileInput) fileInput.value = "";
  };

  /**
   * Handles image reordering through drag and drop
   * @param {Object} event - DnD event object
   */
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setPOldImages(oldImages => {
        const originalPos = oldImages.findIndex(img => img.id === active.id);
        const newPos = oldImages.findIndex(img => img.id === over.id);
        
        if (originalPos !== -1 && newPos !== -1) return arrayMove(oldImages, originalPos, newPos);
        return oldImages;
      });
    }
  };

  return (
    <>
      <div className="flex justify-start items-center rounded-sm m-0 border border-gray-400 py-[20px] px-4 text-neutral-600 gap-GAP-1 w-full relative">
        {/* Image upload button */}
        <div className="flex justify-center items-center border border-solid border-gray-400 rounded-[10px] py-2 px-4">
          <input type="file" id="imageUpload" className="hidden" accept="image/*" multiple
            onChange={(e) => {
              if (e.target.files.length > 0) {
                setModalOpen(true);
                setSelectedFiles(Array.from(e.target.files));
              }
            }}
          />
          <label htmlFor="imageUpload" className="cursor-pointer flex flex-col justify-center items-center gap-0">
            <PiUploadFill className="text-gray-600 text-[2.5rem]" />
            <p className="text-gray-600 text-[1rem] text-light">Upload</p>
          </label>
        </div>

        {/* Draggable image preview container */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToParentElement]}>
          <div className="grow h-full bg-red-20 flex justify-start items-center overflow-x-auto gap-GAP-1 px-2"
            ref={scrollableDivRef} style={{ scrollbarWidth: "none", msOverflowStyle: "none" }} onWheel={handleWheel}
          >
            <SortableContext items={pOldImages?.map((img) => img.id)} strategy={horizontalListSortingStrategy}>
              {pOldImages?.map((image, index) => (
                <ProductFormImage image={image} index={index} key={image.id} handleRemoveOldImage={handleRemoveOldImage} />
              ))}
            </SortableContext>
          </div>
        </DndContext>
      </div>

      {/* Image cropping/preview modal */}
      {modalOpen && (<Modal initialImage={selectedFiles} closeModal={closeModal} updateAvatar={updateImages} useFor="product" />)}
    </>
  );
};

export default MultipleImageInput;